import { doc, getDocFromServer } from 'firebase/firestore';
import { auth, db, functions, httpsCallable } from '@/firebase';
import { deterministicAutoConsequenceId } from './utils/consequenceIdentity';
import { PHASE_EXECUTION_ERROR, phaseExecutionError } from '../domain/phaseRuntime';

/** Persist rule output without replacing the web evaluator or its side effects. */
export async function recordBehaviourAction(data, expectedId, options = {}) {
  if (!auth.currentUser) throw new Error('Sign in before recording a response.');
  const creationType = options.creationType || data.type;
  const id = deterministicAutoConsequenceId({ ...data, type: creationType });
  if (!id || (expectedId && id !== expectedId)) throw new Error('The response recording identity changed. Retry the original response.');
  const action = Object.fromEntries(Object.entries(data).filter(([key, value]) => value !== undefined &&
    !['phaseContext', 'phaseContextSource'].includes(key)));
  for (const key of ['issuedAt', 'createdAt', 'updatedAt']) {
    const date = action[key]?.toDate?.() || action[key];
    if (date != null) action[key] = new Date(date).toISOString();
  }
  let receipt;
  try {
    ({ data: receipt } = await httpsCallable(functions, 'recordBehaviourAction')({
      action, id, creationType, ...(options.phaseSource ? { phaseSource: options.phaseSource } : {}),
      ...(options.accountability ? { accountability: options.accountability } : {}),
      ...(options.escalation ? { escalation: options.escalation } : {}),
      ...(options.threshold ? { threshold: options.threshold } : {}),
    }));
  } catch (error) {
    if (error.details?.phaseExecutionCode === PHASE_EXECUTION_ERROR) throw phaseExecutionError(error.message);
    throw error;
  }
  if (receipt.id !== id || receipt.collection !== 'disciplineQueue') throw new Error('The saved response identity changed. Retry the original response.');
  const saved = await getDocFromServer(doc(db, 'schools', data.schoolId, 'disciplineQueue', id));
  if (!saved.exists()) throw new Error('The saved response could not be read. Retry the original response.');
  return { ...saved.data(), id, wasCreated: receipt.wasCreated };
}

/** Complete an original check only after the server proves no response is due. */
export async function completeBehaviourThresholdCheck(consequence, ruleId) {
  try {
    return (await httpsCallable(functions, 'completeBehaviourThresholdCheck')({
      schoolId: consequence.schoolId, studentId: consequence.studentId, actionId: consequence.id, ruleId,
    })).data;
  } catch (error) {
    if (error.details?.phaseExecutionCode === PHASE_EXECUTION_ERROR) throw phaseExecutionError(error.message);
    throw error;
  }
}
